/**
 * MacOSLayout - Classic Mac OS themed canvas layout
 *
 * Features:
 * - Classic Mac OS menu bar at top
 * - Striped background pattern
 * - System clock
 * - Apple menu
 */
import { memo, useState, useEffect } from 'react';

// ============================================================================
// COMPONENT
// ============================================================================

interface MacOSLayoutProps {
  children: React.ReactNode;
}

export const MacOSLayout = memo(function MacOSLayout({ children }: MacOSLayoutProps) {
  const [time, setTime] = useState(new Date());
  const [appleMenuOpen, setAppleMenuOpen] = useState(false);

  // Update clock every minute
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="macos-layout">
      {/* Classic Mac OS Menu Bar */}
      <div className="macos-menubar">
        <div className="macos-menubar-left">
          {/* Apple Menu */}
          <div className="macos-menu-item apple-menu" onClick={() => setAppleMenuOpen(!appleMenuOpen)}>
            <AppleLogo />
            {appleMenuOpen && (
              <div className="macos-dropdown">
                <div className="macos-dropdown-item">About This Mac</div>
                <div className="macos-dropdown-divider" />
                <div className="macos-dropdown-item">System Preferences...</div>
                <div className="macos-dropdown-item">App Store...</div>
                <div className="macos-dropdown-divider" />
                <div className="macos-dropdown-item">Recent Items</div>
                <div className="macos-dropdown-divider" />
                <div className="macos-dropdown-item">Force Quit...</div>
                <div className="macos-dropdown-divider" />
                <div className="macos-dropdown-item">Sleep</div>
                <div className="macos-dropdown-item">Restart...</div>
                <div className="macos-dropdown-item">Shut Down...</div>
              </div>
            )}
          </div>
          <div className="macos-menu-item"><strong>Finder</strong></div>
          <div className="macos-menu-item">File</div>
          <div className="macos-menu-item">Edit</div>
          <div className="macos-menu-item">View</div>
          <div className="macos-menu-item">Go</div>
          <div className="macos-menu-item">Window</div>
          <div className="macos-menu-item">Help</div>
        </div>
        <div className="macos-menubar-right">
          <div className="macos-status-item">
            <BatteryIcon />
          </div>
          <div className="macos-status-item">
            <WifiIcon />
          </div>
          <div className="macos-status-item macos-clock">
            {formatTime(time)}
          </div>
        </div>
      </div>

      {/* Content area with striped background */}
      <div className="macos-content">
        {children}
      </div>

      {/* Click outside to close menu */}
      {appleMenuOpen && (
        <div
          className="macos-menu-overlay"
          onClick={() => setAppleMenuOpen(false)}
        />
      )}

      <style>{`
        .macos-layout {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          /* Mac OS X Lion aurora/nebula background */
          background:
            radial-gradient(ellipse at 30% 20%, rgba(180, 130, 200, 0.8) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 30%, rgba(120, 100, 180, 0.7) 0%, transparent 45%),
            radial-gradient(ellipse at 50% 80%, rgba(200, 150, 220, 0.6) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 70%, rgba(100, 80, 160, 0.5) 0%, transparent 40%),
            linear-gradient(
              180deg,
              #4a3a6a 0%,
              #6b5a8a 20%,
              #8a7aaa 40%,
              #9a8aba 60%,
              #7a6a9a 80%,
              #5a4a7a 100%
            );
        }

        .macos-menubar {
          height: 22px;
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.85) 0%,
            rgba(230, 230, 230, 0.8) 100%
          );
          border-bottom: 1px solid rgba(0, 0, 0, 0.3);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 10px;
          font-family: -apple-system, "Lucida Grande", "Helvetica Neue", sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: #000;
          user-select: none;
          flex-shrink: 0;
          position: relative;
          z-index: 1000;
        }

        .macos-menubar-left {
          display: flex;
          align-items: center;
          gap: 0;
        }

        .macos-menubar-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .macos-menu-item {
          padding: 2px 12px;
          border-radius: 4px;
          cursor: default;
          position: relative;
        }

        .macos-menu-item:hover {
          background: #3478F6;
          color: white;
        }

        .apple-menu {
          padding: 2px 8px;
        }

        .apple-menu svg {
          width: 14px;
          height: 17px;
        }

        .macos-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          min-width: 200px;
          background: #F5F5F5;
          border: 1px solid #B0B0B0;
          border-radius: 6px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          padding: 4px 0;
          z-index: 1001;
        }

        .macos-dropdown-item {
          padding: 4px 16px;
          cursor: default;
          color: #000;
        }

        .macos-dropdown-item:hover {
          background: #3478F6;
          color: white;
        }

        .macos-dropdown-divider {
          height: 1px;
          background: #D0D0D0;
          margin: 4px 0;
        }

        .macos-status-item {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .macos-status-item svg {
          width: 16px;
          height: 16px;
        }

        .macos-clock {
          font-weight: 500;
          min-width: 70px;
          text-align: right;
        }

        .macos-content {
          flex: 1;
          position: relative;
          overflow: hidden;
          /* Transparent to show aurora background */
          background: transparent;
        }

        .macos-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 999;
        }
      `}</style>
    </div>
  );
});

// ============================================================================
// ICONS
// ============================================================================

function AppleLogo() {
  return (
    <svg viewBox="0 0 14 17" fill="currentColor">
      <path d="M13.1 12.6c-.3.7-.7 1.3-1.1 1.8-.6.8-1.1 1.3-1.5 1.6-.6.5-1.3.7-2 .7-.5 0-1.1-.1-1.8-.4-.7-.3-1.3-.4-1.8-.4-.6 0-1.2.1-1.8.4-.6.3-1.2.4-1.6.4-.7 0-1.4-.2-2-.8-.5-.4-1-1-1.5-1.8C.3 12.6 0 11.1 0 9.6c0-1.4.3-2.5.9-3.5.5-.8 1.1-1.4 1.9-1.8.8-.4 1.7-.7 2.6-.7.5 0 1.2.2 2 .5.8.3 1.3.5 1.5.5.2 0 .8-.2 1.7-.6.9-.3 1.6-.5 2.1-.5 1.6.1 2.8.7 3.6 1.8-1.4.9-2.1 2.1-2 3.6 0 1.2.4 2.2 1.3 3 .4.4.8.7 1.3.9l-.3.8zM10 .5c0 .9-.3 1.8-1 2.6-.8 1-1.8 1.5-2.9 1.4 0-.1 0-.2 0-.4 0-.9.4-1.8 1-2.5.3-.4.7-.7 1.2-1 .5-.2 1-.4 1.4-.4.1.1.1.2.1.3z"/>
    </svg>
  );
}

function BatteryIcon() {
  return (
    <svg viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="19" height="10" rx="2" />
      <rect x="21" y="3.5" width="2" height="5" rx="0.5" fill="currentColor" />
      <rect x="3" y="3" width="15" height="6" rx="1" fill="currentColor" />
    </svg>
  );
}

function WifiIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 18c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm-4.9-2.3l1.4 1.4C9.4 16.4 10.6 16 12 16s2.6.4 3.5 1.1l1.4-1.4C15.6 14.6 13.9 14 12 14s-3.6.6-4.9 1.7zM2.9 10.1l1.4 1.4C6.3 9.6 9 8.5 12 8.5s5.7 1.1 7.7 2.9l1.4-1.4C18.6 7.6 15.5 6.5 12 6.5S5.4 7.6 2.9 10.1z"/>
    </svg>
  );
}
